package com.karawek.core.ui.designcatalog.sections

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.ui.components.CoreLoadingIndicator
import com.karawek.core.ui.components.CoreNoticeCard
import com.karawek.core.ui.components.CoreNoticeTone
import com.karawek.core.ui.components.CorePhoneTextField
import com.karawek.core.ui.components.CoreTag
import com.karawek.core.ui.components.CoreTagTone
import com.karawek.core.ui.theme.CoreAlphas
import com.karawek.core.ui.theme.CoreShapes
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography

@Composable
fun CatalogPreviews() {
    Column(
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.lg),
        modifier = Modifier.padding(CoreSpacing.lg)
    ) {
        SectionHeader("Tags")

        Row(
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        ) {
            CoreTag(text = "None (Default)")
            CoreTag(text = "Info", tone = CoreTagTone.Info)
            CoreTag(text = "Warning", tone = CoreTagTone.Warning)
            CoreTag(text = "Error", tone = CoreTagTone.Error)
        }

        SectionHeader("Notice Cards")

        CoreNoticeCard(
            title = "Info Notice",
            message = "This is an informational notice card with important details.",
            tone = CoreNoticeTone.Info,
        )

        CoreNoticeCard(
            title = "Warning Notice",
            message = "This is a warning notice card that needs attention.",
            tone = CoreNoticeTone.Warning,
        )

        CoreNoticeCard(
            title = "Success Notice",
            message = "This is a success notice card confirming completion.",
            tone = CoreNoticeTone.Success,
        )

        SectionHeader("Phone Text Field")

        CorePhoneTextField(
            value = "5551234567",
            onValueChange = {},
            label = "Phone number",
            placeholder = "(555) 123-4567",
        )

        SectionHeader("OTP Channel Chip")

        Row(
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        ) {
            OtpChannelChip(
                selected = false,
                enabled = true,
                label = "Phone",
                onClick = {},
            )

            OtpChannelChip(
                selected = true,
                enabled = true,
                label = "Email",
                onClick = {},
            )

            OtpChannelChip(
                selected = false,
                enabled = false,
                label = "Disabled",
                onClick = {},
            )
        }

        SectionHeader("Verification Badge")

        Row(
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        ) {
            VerifiedBadge()
            PendingBadge()
            UnverifiedBadge()
        }
    }
}

@Composable
private fun SectionHeader(title: String) {
    Text(
        text = title,
        style = CoreTypography.title,
    )
}

@Composable
private fun RowScope.OtpChannelChip(
    selected: Boolean,
    enabled: Boolean = true,
    label: String,
    onClick: () -> Unit,
) {
    val colors = CoreTheme.colors

    val background = when {
        !enabled -> colors.surface.copy(alpha = CoreAlphas.DisabledSurface)
        selected -> colors.primary.copy(alpha = CoreAlphas.SelectedSurface)
        else -> colors.surface
    }

    val borderColor = when {
        !enabled -> colors.divider.copy(alpha = CoreAlphas.Disabled)
        selected -> colors.primary
        else -> colors.divider
    }

    val textColor = when {
        !enabled -> colors.textPrimary.copy(alpha = CoreAlphas.Disabled)
        else -> colors.textPrimary
    }

    Surface(
        onClick = onClick,
        enabled = enabled,
        shape = CoreShapes.sm,
        color = background,
        border = BorderStroke(1.dp, borderColor),
        tonalElevation = if (selected) 1.dp else 0.dp,
        shadowElevation = if (selected) 1.dp else 0.dp,
        modifier = Modifier.weight(1f),
    ) {
        Box(
            modifier = Modifier.padding(CoreSpacing.sm),
            contentAlignment = Alignment.Center,
        ) {
            Text(text = label, color = textColor)
        }
    }
}

@Composable
private fun VerifiedBadge() {
    Surface(
        shape = CoreShapes.lg,
        color = CoreTheme.colors.surfaceVariant,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = CoreSpacing.md, vertical = CoreSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
        ) {
            Icon(
                imageVector = Icons.Filled.CheckCircle,
                contentDescription = null,
                tint = CoreTheme.colors.onBackground,
                modifier = Modifier.size(18.dp),
            )
            Text(
                text = "Verified",
                style = CoreTypography.body,
                color = CoreTheme.colors.textPrimary,
            )
        }
    }
}

@Composable
private fun PendingBadge() {
    Surface(
        shape = CoreShapes.lg,
        color = CoreTheme.colors.surfaceVariant,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = CoreSpacing.md, vertical = CoreSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        ) {
            CoreLoadingIndicator(modifier = Modifier.size(18.dp))
            Text(
                text = "Verifying…",
                style = CoreTypography.body,
                color = CoreTheme.colors.textSecondary,
            )
        }
    }
}

@Composable
private fun UnverifiedBadge() {
    Surface(
        onClick = {},
        enabled = true,
        shape = CoreShapes.lg,
        color = CoreTheme.colors.surface,
        border = BorderStroke(1.dp, CoreTheme.colors.divider),
    ) {
        Text(
            text = "Verify",
            style = CoreTypography.body,
            color = CoreTheme.colors.textPrimary,
            modifier = Modifier.padding(horizontal = CoreSpacing.md, vertical = CoreSpacing.sm),
        )
    }
}
